import AddIcon from '@mui/icons-material/Add';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  MenuItem,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { adminUsersApi, type AdminUser } from '../../api/admin-users.api.js';
import { settingsHistoryKey } from '../../api/settings-history.api.js';
import type { AdminRole } from '../../api/auth.api.js';
import { ROLE_DESCRIPTIONS, ROLE_LABELS, roleLabel } from '../../auth/capabilities.js';
import { useAuth } from '../../auth/useAuth.js';
import { StatusBadge } from '../../components/StatusBadge.js';

import { APP_LOCALE } from '../../utils/locale.js';
import { SettingsHistoryButton, useSettingsLastChanged } from '../settings/SettingsHistory.js';
import { noRestoreNote } from '../settings/settings-sections.js';
import { SettingsSectionHeader } from '../settings/SettingsSectionHeader.js';

import { CAN_APPROVE_EDD, eddPermissionControl, withPermission } from './staff-permissions.js';

/** The five roles an administrator grants (DEV-67), in console order. */
const CREATE_ROLES: readonly AdminRole[] = ['admin', 'compliance', 'client_manager', 'finance', 'support'];

export function AdminUsersPage(): JSX.Element {
  const qc = useQueryClient();
  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState({
    email: '',
    password: '',
    role: 'support' as AdminRole,
    mfaRequired: true,
  });
  const [error, setError] = useState<string | null>(null);
  const { user: me } = useAuth();
  const lastChanged = useSettingsLastChanged('ADMIN_USERS');
  // Every staff-account change lands in the History (DEV-137); refresh it after one.
  const refreshHistory = (): void => {
    void qc.invalidateQueries({ queryKey: settingsHistoryKey('ADMIN_USERS') });
  };
  const [editing, setEditing] = useState<AdminUser | null>(null);
  const [newRole, setNewRole] = useState<AdminRole>('support');
  const [roleError, setRoleError] = useState<string | null>(null);

  const usersQuery = useQuery({
    queryKey: ['admin-users'],
    queryFn: () => adminUsersApi.list(),
    refetchOnWindowFocus: true,
  });

  const createMutation = useMutation({
    mutationFn: () =>
      adminUsersApi.create({
        email: form.email,
        password: form.password,
        roles: [form.role],
        mfaRequired: form.mfaRequired,
      }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['admin-users'] });
      refreshHistory();
      setCreateOpen(false);
      setForm({ email: '', password: '', role: 'support', mfaRequired: true });
      setError(null);
    },
    onError: (err: unknown) => setError(err instanceof Error ? err.message : 'Create failed'),
  });

  const roleMutation = useMutation({
    mutationFn: (args: { id: string; role: AdminRole }) => adminUsersApi.setRoles(args.id, [args.role]),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['admin-users'] });
      refreshHistory();
      setEditing(null);
    },
    onError: (err: unknown) => setRoleError(err instanceof Error ? err.message : 'The role could not be changed.'),
  });
  // Admin rights are taken away by another administrator, never by the holder.
  const isSelfAdmin = (user: AdminUser): boolean => user.id === me?.id && user.roles.includes('admin');

  // EDD approval (can_approve_edd, DEV-112): clearing or keeping "EDD required".
  const [permissionError, setPermissionError] = useState<string | null>(null);
  const permissionMutation = useMutation({
    mutationFn: (args: { user: AdminUser; on: boolean }) =>
      adminUsersApi.setPermissions(args.user.id, withPermission(args.user.permissions ?? [], CAN_APPROVE_EDD, args.on)),
    onSuccess: async () => {
      setPermissionError(null);
      await qc.invalidateQueries({ queryKey: ['admin-users'] });
      refreshHistory();
    },
    onError: (err: unknown) =>
      setPermissionError(err instanceof Error ? err.message : 'The permission could not be changed.'),
  });

  const mfaMutation = useMutation({
    mutationFn: (args: { id: string; required: boolean }) =>
      adminUsersApi.setMfa(args.id, args.required),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['admin-users'] });
      refreshHistory();
    },
  });

  return (
    <Box>
      <SettingsSectionHeader
        title="Admin users"
        lastChanged={lastChanged}
        actions={
          <>
            <SettingsHistoryButton subject="ADMIN_USERS" title="Admin users" restoreNote={noRestoreNote({ group: 'access', id: 'admin-users' })} />
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreateOpen(true)}>
              New staff user
            </Button>
          </>
        }
      >
        Create staff accounts and assign their access. Finance users can monitor transactions and must enroll an
        authenticator at first sign-in. Account creation, role, permission and second-factor changes are recorded in the History.
      </SettingsSectionHeader>

      {usersQuery.isError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to load admins: {usersQuery.error instanceof Error ? usersQuery.error.message : 'unknown'}
        </Alert>
      )}

      {permissionError !== null && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setPermissionError(null)}>
          {permissionError}
        </Alert>
      )}

      <Table>
        <TableHead>
          <TableRow>
            <TableCell>Email</TableCell>
            <TableCell>Roles</TableCell>
            <TableCell>
              <Tooltip title='Can clear or keep "EDD required" for a customer with no EDD case. Nobody holds it by default.'>
                <span>EDD approval</span>
              </Tooltip>
            </TableCell>
            <TableCell>MFA required</TableCell>
            <TableCell>TOTP enrolled</TableCell>
            <TableCell>Last login</TableCell>
            <TableCell>Created</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {usersQuery.data?.length === 0 && (
            <TableRow>
              <TableCell colSpan={7} align="center" sx={{ color: 'text.secondary', py: 6 }}>
                No admin users yet.
              </TableCell>
            </TableRow>
          )}
          {usersQuery.data?.map((user: AdminUser) => (
            <TableRow key={user.id} hover>
              <TableCell sx={{ fontFamily: 'var(--font-mono)', fontSize: 14 }}>{user.email}</TableCell>
              <TableCell>
                <Stack direction="row" gap={1} alignItems="center">
                  {user.roles.map((r) => (
                    <Chip key={r} size="small" label={roleLabel(r)} color="primary" variant="outlined" />
                  ))}
                  <Button
                    size="small"
                    aria-label={`Change role of ${user.email}`}
                    onClick={() => {
                      setEditing(user);
                      setNewRole(user.roles[0] ?? 'support');
                      setRoleError(null);
                    }}
                  >
                    Change
                  </Button>
                </Stack>
              </TableCell>
              <TableCell>
                <EddApprovalSwitch
                  user={user}
                  meId={me?.id}
                  pending={permissionMutation.isPending}
                  onChange={(on) => permissionMutation.mutate({ user, on })}
                />
              </TableCell>
              <TableCell>
                <Tooltip
                  title={
                    user.mfaRequired
                      ? 'TOTP is required at every sign-in.'
                      : 'This account can sign in with email + password alone.'
                  }
                >
                  <FormControlLabel
                    control={
                      <Switch
                        checked={user.mfaRequired}
                        disabled={mfaMutation.isPending || user.roles.includes('finance')}
                        inputProps={{ 'aria-label': `TOTP MFA required for ${user.email}` }}
                        onChange={(e) =>
                          mfaMutation.mutate({ id: user.id, required: e.target.checked })
                        }
                      />
                    }
                    label={user.mfaRequired ? 'On' : 'Off'}
                  />
                </Tooltip>
              </TableCell>
              <TableCell>
                <StatusBadge kind="mfa" value={user.totpEnabled ? 'ENABLED' : 'DISABLED'} />
              </TableCell>
              <TableCell sx={{ color: 'text.secondary' }}>
                {user.lastLoginAt !== null ? new Date(user.lastLoginAt).toLocaleString(APP_LOCALE) : '—'}
              </TableCell>
              <TableCell sx={{ color: 'text.secondary' }}>
                {new Date(user.createdAt).toLocaleDateString(APP_LOCALE)}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <Dialog open={editing !== null} onClose={() => { if (!roleMutation.isPending) setEditing(null); }} fullWidth maxWidth="xs" aria-labelledby="change-role-title">
        <DialogTitle id="change-role-title">Change role</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Typography variant="body2" sx={{ fontFamily: 'var(--font-mono)' }}>{editing?.email}</Typography>
            <TextField select label="Role" value={newRole} onChange={(e) => setNewRole(e.target.value as AdminRole)}
              helperText={ROLE_DESCRIPTIONS[newRole]}>
              {CREATE_ROLES.map((role) => (
                <MenuItem key={role} value={role} disabled={editing !== null && isSelfAdmin(editing) && role !== 'admin'}>
                  {ROLE_LABELS[role]}
                </MenuItem>
              ))}
            </TextField>
            {editing !== null && isSelfAdmin(editing) ? (
              <Alert severity="info">Your own admin role can only be removed by another administrator.</Alert>
            ) : null}
            {newRole === 'finance' ? (
              <Alert severity="info">Finance accounts always require an authenticator at sign-in.</Alert>
            ) : null}
            <Typography variant="caption" color="text.secondary">
              The change applies from their next sign-in or session refresh, and is recorded in the audit log.
            </Typography>
            {roleError !== null ? <Alert severity="error">{roleError}</Alert> : null}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditing(null)} disabled={roleMutation.isPending}>Cancel</Button>
          <Button
            variant="contained"
            disabled={roleMutation.isPending || editing === null || editing.roles.length === 1 && editing.roles[0] === newRole}
            onClick={() => { if (editing !== null) roleMutation.mutate({ id: editing.id, role: newRole }); }}
          >
            {roleMutation.isPending ? 'Saving…' : 'Save role'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={createOpen} onClose={() => { if (!createMutation.isPending) setCreateOpen(false); }} fullWidth maxWidth="sm">
        <Box component="form" onSubmit={(event) => { event.preventDefault(); createMutation.mutate(); }}>
        <DialogTitle>Create staff user</DialogTitle>
        <DialogContent>
          {error !== null && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          <Stack spacing={2} sx={{ mt: 2 }}>
            <TextField
              label="Email"
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              required
              fullWidth
            />
            <TextField
              label="Initial password"
              type="password"
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              required
              fullWidth
              inputProps={{ minLength: 12, maxLength: 256 }}
              helperText="Use 12 or more characters, with an uppercase letter, a number and a symbol. Share the initial password securely."
            />
            <TextField
              select
              label="Role"
              value={form.role}
              onChange={(e) => setForm({ ...form, role: e.target.value as AdminRole, mfaRequired: e.target.value === 'finance' || form.mfaRequired })}
              helperText={ROLE_DESCRIPTIONS[form.role]}
              fullWidth
            >
              {CREATE_ROLES.map((r) => (
                <MenuItem key={r} value={r}>{ROLE_LABELS[r]}</MenuItem>
              ))}
            </TextField>
            <FormControlLabel
              control={
                <Switch
                  checked={form.mfaRequired}
                  disabled={form.role === 'finance'}
                  onChange={(e) => setForm({ ...form, mfaRequired: e.target.checked })}
                />
              }
              label={
                <Box>
                  <Typography variant="body2">Require TOTP MFA</Typography>
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    Finance access always requires an authenticator.
                  </Typography>
                </Box>
              }
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button disabled={createMutation.isPending} onClick={() => setCreateOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={createMutation.isPending}
            type="submit"
          >
            {createMutation.isPending ? 'Creating…' : 'Create user'}
          </Button>
        </DialogActions>
        </Box>
      </Dialog>
    </Box>
  );
}

/** The per-row "EDD approval" switch, with the reason when it cannot be used. */
function EddApprovalSwitch({ user, meId, pending, onChange }: {
  user: AdminUser;
  meId: string | undefined;
  pending: boolean;
  onChange: (on: boolean) => void;
}): JSX.Element {
  const control = eddPermissionControl(meId === undefined ? null : { id: meId }, user);
  return (
    <Tooltip title={control.hint ?? (control.checked ? 'Holds the EDD-approval permission.' : 'Grant the EDD-approval permission.')}>
      <FormControlLabel
        control={
          <Switch
            checked={control.checked}
            disabled={pending || control.disabled}
            inputProps={{ 'aria-label': `EDD approval for ${user.email}` }}
            onChange={(e) => onChange(e.target.checked)}
          />
        }
        label={control.checked ? 'Granted' : 'Not granted'}
      />
    </Tooltip>
  );
}
